import { removedCellClassName } from '@helpers';
import { Box, Link, Tooltip } from '@mui/material';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';

import { ITableLinksManagement } from '@organisms/Tables/Enterprise/LinksManagementTable/types';
import {
  getConfigCategoryLinkManagement,
  getConfigIdentifiedTypeLinkManagement,
  getConfigPriorityLinkManagement,
  getConfigStatusLinkManagement,
  getLinksPriorityRange,
} from '@organisms/Tables/Enterprise/LinksManagementTable/utils';

export interface CSVData {
  url: string;
  category: string;
  asset_title: string;
  priority: string;
  status: string;
  identified_type: string;
  index: number;
}

export const formatErrorWithIndexes = (errorMessage: string, indexes: number[]) => {
  let start = indexes[0];
  let end = indexes[0];
  const errorLines: string[] = [];
  indexes.forEach((value, index) => {
    if (index !== 0) {
      if (value - 1 === end) {
        end = value;
      } else {
        start === end
          ? errorLines.push(`line ${start + 1} `)
          : errorLines.push(`${start + 1}~${end + 1} lines`);
        start = value;
        end = value;
      }
    }

    if (index === indexes.length - 1) {
      start === end
        ? errorLines.push(` line ${start + 1} `)
        : errorLines.push(`${start + 1}~${end + 1} lines`);
    }
  });

  return `${errorMessage} (${errorLines.join(',')})`;
};

export const columns: GridColDef<ITableLinksManagement>[] = [
  {
    field: 'url',
    headerName: 'Link',
    minWidth: 250,
    flex: 1,
    cellClassName: removedCellClassName,
    valueGetter: (params: GridValueGetterParams) => params.row.url,
    renderCell: ({ value }) => (
      <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
        <Tooltip title={value}>
          <Link
            className="ellipsis"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              width: '90%',
            }}
            href={value}
            target="_blank"
          >
            {value}
          </Link>
        </Tooltip>
      </Box>
    ),
    filterable: false,
    sortable: false,
  },
  {
    field: 'category',
    headerName: 'Category',
    minWidth: 150,
    flex: 0.7,
    cellClassName: removedCellClassName,
    resizable: false,
    sortable: false,
    filterable: false,
    renderCell: ({ value }) => getConfigCategoryLinkManagement(value),
  },
  {
    field: 'asset_title',
    headerName: 'Asset title',
    minWidth: 200,
    flex: 1,
    cellClassName: removedCellClassName,
    renderCell: ({ value }) => (
      <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
        <Tooltip title={value}>
          <span
            className="ellipsis"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              width: '90%',
            }}
          >
            {value}
          </span>
        </Tooltip>
      </Box>
    ),
    resizable: false,
    sortable: false,
    filterable: false,
  },
  {
    field: 'priority',
    headerName: 'Priority',
    minWidth: 100,
    maxWidth: 150,
    flex: 0.7,
    cellClassName: removedCellClassName,
    valueGetter: (params: GridValueGetterParams) => params.row.priority,
    renderCell: ({ value }) => {
      const convertedValue = getLinksPriorityRange(+value);
      if (!convertedValue) return '';

      const config = getConfigPriorityLinkManagement(convertedValue);
      return (
        <span
          style={{
            color: config?.color,
          }}
        >
          {config?.priority}
        </span>
      );
    },
    resizable: false,
    sortable: false,
    filterable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 100,
    maxWidth: 300,
    flex: 1,
    cellClassName: removedCellClassName,
    valueGetter: (params: GridValueGetterParams) => params.row.status,
    renderCell: ({ value }) => {
      const statusConfig = getConfigStatusLinkManagement(value);
      return <span>{statusConfig?.status}</span>;
    },
    resizable: false,
    sortable: false,
    filterable: false,
  },
  {
    field: 'identified_type',
    headerName: 'Identified type',
    minWidth: 120,
    maxWidth: 300,
    flex: 1,
    headerAlign: 'left',
    sortable: false,
    align: 'left',
    renderCell: ({ value }) => {
      const config = getConfigIdentifiedTypeLinkManagement(value);
      return <span>{config?.identifiedType}</span>;
    },
  },
];
